import React from "react";
import { useHistory } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import TypeShowcase from "../../../components/landing/TypeShowcase";
import type { ListingSubcategory } from "../../../components/landing/ListingGrid";
import { PLAY_COLORS, playSearchUrl } from "./PlayIntro";

const availableFooter = (
  <div className="flex items-center justify-between gap-[12px] text-[12px] leading-4">
    <span className="flex items-center gap-1.5 font-bold text-[#0a0a0a]">
      <span className="size-2 rounded-full bg-[#22c55e]" />
      Available now
    </span>
    <span className="text-[#717182]">Book instantly</span>
  </div>
);

const ADVENTURE_INCLUDED = [
  "Hiking and nature walk bookings",
  "Guided tours and local experiences",
  "Verified local tour operators",
  "Individual and group options",
];

const ADVENTURE_WHY = [
  "Discover new destinations with confidence",
  "Access to trusted local guides and operators",
  "Balance work and travel with time outdoors",
  "Integrated with SPACIA Work and Stay for a seamless travel experience",
];

function AdventureSection({ subcategories }: PlayTypesProps) {
  const history = useHistory();
  const color = PLAY_COLORS.adventure;

  return (
    <section className="bg-[#f8fbff] py-20">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-12 px-[16px] sm:px-8 lg:grid-cols-2">
        {/* The Figma frame leaves the inset photo and chip floating below a 320px photo;
            here the photo fills the column so they overlap it, as on the other sections. */}
        <div className="relative pb-[60px] sm:h-[500px] sm:pb-0">
          <div className="relative h-[320px] overflow-hidden rounded-3xl sm:h-[500px]">
            <img
              src="/figma-assets/play/adventure.jpg"
              alt="Adventure"
              className="absolute inset-0 size-full object-cover"
            />
            <span
              className="absolute left-[20px] top-[20px] rounded-full px-[12px] py-[5px] text-[12px] font-bold uppercase leading-4 tracking-[1.2px] text-white"
              style={{ backgroundColor: color }}
            >
              Adventure
            </span>
          </div>
          <img
            src="/figma-assets/play/adventure-detail.jpg"
            alt=""
            className="absolute bottom-0 right-[16px] h-[120px] w-[156px] rounded-2xl border-4 border-solid border-white object-cover shadow-xl sm:bottom-[20px] sm:h-[160px] sm:w-[208px]"
          />
          <span className="absolute bottom-[12px] left-0 flex items-center gap-2 rounded-full border-[1px] border-solid border-[#e5e7eb] bg-white px-[16px] py-2 text-[12px] font-semibold leading-4 text-[#0a0a0a] shadow-md sm:bottom-[20px] sm:left-[20px]">
            <span className="size-2 rounded-full bg-[#22c55e]" />
            Verified guide
          </span>
        </div>

        <div className="lg:pt-8">
          <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px]" style={{ color }}>
            SPACIA PLAY
          </p>
          <h2 className="pt-[12px] text-[30px] font-bold leading-10 tracking-[-0.9px] text-[#0a0a0a] sm:text-[36px]">
            Adventure
          </h2>
          <p className="pt-[16px] text-[16px] leading-[26px] text-[#717182]">
            Explore the outdoors with hikes, nature walks and guided experiences.
          </p>

          <p className="pt-8 text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-[#0a0a0a]">
            What's included
          </p>
          <ul className="space-y-2.5 pt-[16px]">
            {ADVENTURE_INCLUDED.map(item => (
              <li key={item} className="flex items-start gap-2.5 text-[14px] leading-5 text-[#3d3d4d]">
                <span
                  className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${color}26` }}
                >
                  <Check className="size-2.5" style={{ color }} />
                </span>
                {item}
              </li>
            ))}
          </ul>

          <div className="my-8 rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white p-[20px]">
            <p className="text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-[#0a0a0a]">
              Why it works
            </p>
            <ul className="space-y-2 pt-[12px]">
              {ADVENTURE_WHY.map(item => (
                <li key={item} className="flex items-start gap-2 text-[14px] leading-5 text-[#717182]">
                  <ArrowRight className="mt-0.5 size-[13px] shrink-0" style={{ color }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <button
            type="button"
            onClick={() => history.push(playSearchUrl("adventure", subcategories))}
            className="inline-flex items-center gap-2 text-[14px] font-semibold leading-5 hover:underline"
            style={{ color }}
          >
            Explore Adventure
            <ArrowRight className="size-[15px]" />
          </button>
        </div>
      </div>
    </section>
  );
}

type PlayTypesProps = { subcategories: ListingSubcategory[] };

export default function PlayTypes({ subcategories }: PlayTypesProps) {
  const history = useHistory();

  return (
    <>
      <TypeShowcase
        eyebrow="SPACIA PLAY"
        name="Wellness"
        color={PLAY_COLORS.wellness}
        background="bg-[#f8fbff]"
        image="/figma-assets/play/wellness.jpg"
        imageSide="left"
        imageHeight={460}
        compactFooter
        imageFooter={availableFooter}
        checkTint="17"
        description="Book spa treatments, fitness classes and wellness experiences to support a balanced lifestyle."
        included={[
          "Spa and massage bookings",
          "Yoga and fitness classes",
          "Wellness retreats and packages",
          "Verified wellness providers",
          "Holistic wellness experiences",
        ]}
        whyItWorks={[
          "Promote relaxation and stress management",
          "Convenient access to trusted wellness services",
          "Support for a healthier, more balanced routine",
          "Book wellness time around work and travel",
        ]}
        cta="Explore Wellness"
        onCta={() => history.push(playSearchUrl("wellness", subcategories))}
      />
      <TypeShowcase
        eyebrow="SPACIA PLAY"
        name="Dining"
        color={PLAY_COLORS.dining}
        background="bg-white"
        image="/figma-assets/play/dining.jpg"
        imageSide="right"
        imageHeight={460}
        compactFooter
        detailImage="/figma-assets/play/dining-detail.jpg"
        imageFooter={availableFooter}
        checkTint="17"
        description="Discover and reserve quality dining experiences, wherever you are."
        included={[
          "Curated restaurant listings",
          "Instant table reservations",
          "Reviews and ratings",
          "Cuisine, location and price filters",
          "Curated dining experiences",
        ]}
        whyItWorks={[
          "Discover diverse culinary experiences",
          "Simple, instant reservations",
          "Support for local restaurants and independent venues",
          "Access trusted dining establishments",
        ]}
        cta="Explore Dining"
        onCta={() => history.push(playSearchUrl("dining", subcategories))}
      />
      <AdventureSection subcategories={subcategories} />
    </>
  );
}
